const VULCAN_CLOUDS = ['PHOCAS', 'EPICOR'] as const
export type VulcanCloud = (typeof VULCAN_CLOUDS)[number]

const VULCAN_REGIONS = [
  { id: 'AUS_SYD', code: 'syd', label: 'Sydney' },
  { id: 'CAN_MTL', code: 'mtl', label: 'Montreal' },
  { id: 'EUR_FRA', code: 'fra', label: 'Frankfurt' },
  { id: 'UKS_LON', code: 'lon', label: 'London' },
  { id: 'USA_ORE', code: 'ore', label: 'Oregon' },
  { id: 'USA_VIR', code: 'vir', label: 'Virginia' }
] as const

const VULCAN_ENVIRONMENTS = [
  { id: 'local', label: 'Local', domain: null, defaultRegionId: 'LOCAL' },
  { id: 'cloud-dev', label: 'Cloud Dev', domain: 'phocasclouddev.com', defaultRegionId: 'USA_ORE' },
  { id: 'preview', label: 'Preview', domain: 'phocaspreview.com', defaultRegionId: 'USA_ORE' },
  { id: 'production', label: 'Production', domain: 'phocassoftware.com', defaultRegionId: 'USA_ORE' },
  { id: 'ninja', label: 'Ninja', domain: 'phocas.ninja', defaultRegionId: 'AUS_SYD' }
] as const

export type VulcanEnvironmentId = (typeof VULCAN_ENVIRONMENTS)[number]['id']
export type VulcanRegionId = (typeof VULCAN_REGIONS)[number]['id'] | 'LOCAL'
export type VulcanGatewayKey = `${VulcanCloud}:${VulcanRegionId}`

export interface GatewayDefinition {
  environmentId: VulcanEnvironmentId
  environmentLabel: string
  isEnvironmentDefault: boolean
  cloud: VulcanCloud
  regionId: VulcanRegionId
  regionLabel: string
  baseUrl: string
  key: VulcanGatewayKey
  label: string
}

function gatewayUrl(domain: string, cloud: VulcanCloud, regionCode: string): string {
  const prefix = cloud === 'EPICOR' ? 'api-epi' : 'api'
  return `https://${prefix}-${regionCode}.${domain}`
}

export function gatewayKey(cloud: VulcanCloud, regionId: VulcanRegionId): VulcanGatewayKey {
  return `${cloud}:${regionId}`
}

function createGatewayDefinitions(): readonly GatewayDefinition[] {
  const gateways: GatewayDefinition[] = []
  for (const environment of VULCAN_ENVIRONMENTS) {
    if (!environment.domain) {
      gateways.push({
        environmentId: environment.id,
        environmentLabel: environment.label,
        isEnvironmentDefault: true,
        cloud: 'PHOCAS',
        regionId: 'LOCAL',
        regionLabel: 'Local gateway',
        baseUrl: 'http://localhost:8080',
        key: gatewayKey('PHOCAS', 'LOCAL'),
        label: `${environment.label} · Local gateway`
      })
      continue
    }

    for (const cloud of VULCAN_CLOUDS) {
      for (const region of VULCAN_REGIONS) {
        gateways.push({
          environmentId: environment.id,
          environmentLabel: environment.label,
          isEnvironmentDefault: cloud === 'PHOCAS' && region.id === environment.defaultRegionId,
          cloud,
          regionId: region.id,
          regionLabel: region.label,
          baseUrl: gatewayUrl(environment.domain, cloud, region.code),
          key: gatewayKey(cloud, region.id),
          label: `${environment.label} · ${cloud} · ${region.label}`
        })
      }
    }
  }
  return gateways
}

export const VULCAN_GATEWAYS = createGatewayDefinitions()

export function createGatewayMatrix(
  gateways: readonly GatewayDefinition[] = VULCAN_GATEWAYS
): Map<VulcanEnvironmentId, Map<VulcanGatewayKey, GatewayDefinition>> {
  const matrix = new Map<VulcanEnvironmentId, Map<VulcanGatewayKey, GatewayDefinition>>()
  for (const gateway of gateways) {
    const environmentGateways = matrix.get(gateway.environmentId) ?? new Map<VulcanGatewayKey, GatewayDefinition>()
    environmentGateways.set(gateway.key, gateway)
    matrix.set(gateway.environmentId, environmentGateways)
  }
  return matrix
}

const TOOLS_ENVIRONMENTS: Readonly<Record<string, VulcanEnvironmentId>> = {
  'tools.phocasclouddev.com': 'cloud-dev',
  'tools.phocaspreview.com': 'preview',
  'tools.phocas.io': 'production'
}

export function defaultGateway(hostname = window.location.hostname): GatewayDefinition {
  const configuredBaseUrl = import.meta.env.VITE_VULCAN_AUTH_BASE_URL
  const defaultEnvironmentId = import.meta.env.DEV ? 'local' : (TOOLS_ENVIRONMENTS[hostname.toLowerCase()] ?? 'preview')
  const defaultKey = gatewayKey('PHOCAS', defaultEnvironmentId === 'local' ? 'LOCAL' : 'USA_ORE')
  const gateway = VULCAN_GATEWAYS.find(
    (candidate) => candidate.environmentId === defaultEnvironmentId && candidate.key === defaultKey
  )
  if (!gateway) throw new Error(`No default Vulcan gateway is configured for ${defaultEnvironmentId}.`)
  return configuredBaseUrl
    ? {
        ...gateway,
        baseUrl: configuredBaseUrl,
        label: `${gateway.label} · ${new URL(configuredBaseUrl, window.location.href).host}`
      }
    : gateway
}

export function findGateway(
  environmentId: VulcanEnvironmentId,
  cloud: VulcanCloud,
  regionId: VulcanRegionId,
  gateways: readonly GatewayDefinition[] = VULCAN_GATEWAYS
): GatewayDefinition | undefined {
  return gateways.find(
    (gateway) => gateway.environmentId === environmentId && gateway.key === gatewayKey(cloud, regionId)
  )
}
